import { omit } from "lodash-es";
import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { ExportICalForm } from "Bands/components";
import config from "config";
import { copy } from "shared/assets";
import { Download } from "shared/assets/icons";
import { ClipboardField, Loader, Message, Modal } from "shared/components";
import { Spacer } from "shared/layout";
import { IAPIState, IStatus } from "shared/types";

import { editBandIcal } from "Bands/state";
import { IBand, IBandIcalParams } from "Bands/types";
import { useModalLogic } from "./useModalLogic";
import { BandService } from "Bands/services";

const { calendar: calendarCopy } = copy;
interface ExportICalModalProps {
  handleClose: () => void;
  band: IBand;
}

export const ExportICalModal: FC<ExportICalModalProps> = ({ handleClose, band }) => {
  const [selectedBand, setSelectedBand] = useState<IBand | null>(null);

  const eventTypes = useMemo(
    () =>
      selectedBand
        ? Object.values((selectedBand as unknown as IBand)?.eventTypes || []).map(
            ({ data: { attributes } }: any) => attributes
          )
        : [],
    [selectedBand]
  );

  const { editApiStatus, iCal, defaultValues } = useModalLogic({
    bandId: band.id,
    band,
    eventTypes,
  });

  const dispatch = useDispatch();

  const handleSubmit = (data: IBandIcalParams) => {
    if (!iCal) return;

    const cleanData = {
      ...omit(data, "eventRsvpStatuses"),
      eventRSVPStatuses: data.eventRsvpStatuses || [],
    };

    dispatch(
      editBandIcal({
        id: iCal?.id,
        data: cleanData,
      })
    );
  };

  useEffect(() => {
    const fetchBand = async () => {
      try {
        const bandData = await BandService.getBand(band.id);

        setSelectedBand(Object.values(bandData.band)?.[0].attributes as unknown as IBand);
      } catch (error) {
        console.error("Failed to fetch band data", error);
      }
    };

    fetchBand();
  }, [band.id]);

  return (
    <Modal
      icon={Download}
      title={calendarCopy.iCalExport.title}
      dataTestId="export_ical_modal"
      handleClose={handleClose}
    >
      <Spacer size="l">
        <Message message={calendarCopy.iCalExport.infoMessage} status={IStatus.info} />
        {!iCal && <Loader size="s" />}
        {iCal && (
          <Spacer size="l">
            <ClipboardField
              label={calendarCopy.iCalExport.link}
              copyText={`${config.app}/ical/${iCal.id}`}
            />
            <ExportICalForm
              defaultValues={defaultValues}
              eventTypes={eventTypes}
              onCancel={handleClose}
              handleSubmit={handleSubmit}
              apiStatus={editApiStatus}
              hasError={editApiStatus === IAPIState.failed}
            />
          </Spacer>
        )}
      </Spacer>
    </Modal>
  );
};
